import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserPlus } from "lucide-react";

import { useToast } from "@/hooks/useToast";
import { copyTextToClipboard } from "@/lib/clipboard";
import { buildGuestInviteLink } from "@/lib/guestInviteLink";
import {
  MEETING_GUEST_LINK_TTL_SECONDS,
  readModeratorClaim,
} from "@/lib/meetToken";
import { featureFlags } from "@/config/featureFlags";
import { createMeetingGuestInvite } from "@/server/services/inviteService";
import MeetConference from "./MeetConference";

/**
 * Standalone-meeting JOIN page (route `/meet/:meetingId`, Product B V0).
 *
 * Thin wrapper over the shared `MeetConference` (which owns the mint state
 * machine, generation guards, moderation stack and Jitsi embed). This page adds
 * the two `/meet/:meetingId`-specific bits: the shareable-link string and the
 * OWNER-only "copy guest link" action.
 *
 * Authenticated + flag-gated via `standaloneMeetRoutes.tsx`.
 */
export default function MeetJoinPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { meetingId } = useParams<{ meetingId: string }>();

  // Loading guard for the owner-only "copy guest link" action — the create
  // endpoint is rate-limited, so a double-click must not double-POST. The REF is
  // the authoritative guard (checked+set synchronously); the state drives the UI.
  const [isCreatingGuestLink, setIsCreatingGuestLink] = useState(false);
  const creatingGuestLinkRef = useRef(false);

  // Shareable link. `<origin>/meet/<meetingId>` FOR NOW — at prod promotion
  // Product B moves to the dedicated `meet.orionmessenger.io` host (PRD §7).
  const shareLink = `${window.location.origin}/meet/${meetingId}`;

  async function handleCopyGuestLink() {
    // Synchronous ref guard first — state updates are async, so two clicks in
    // the same render tick could both see isCreatingGuestLink === false.
    if (!meetingId || creatingGuestLinkRef.current) {
      return;
    }
    creatingGuestLinkRef.current = true;
    setIsCreatingGuestLink(true);
    try {
      const { inviteCode } = await createMeetingGuestInvite({
        meetingId,
        ttlSeconds: MEETING_GUEST_LINK_TTL_SECONDS,
      });
      // maxJoins intentionally omitted: unlimited-within-TTL, like a Zoom link.
      const ok = await copyTextToClipboard(buildGuestInviteLink(inviteCode));
      toast({
        duration: 2000,
        description: ok
          ? t("standaloneMeet.guestLinkCopied")
          : t("standaloneMeet.guestLinkFailed"),
        ...(ok ? {} : { variant: "destructive" as const }),
      });
    } catch {
      // Never surface the raw error (may echo a JSON backend body) or log the
      // invite code — a generic failure toast only.
      toast({
        duration: 2000,
        description: t("standaloneMeet.guestLinkFailed"),
        variant: "destructive",
      });
    } finally {
      creatingGuestLinkRef.current = false;
      setIsCreatingGuestLink(false);
    }
  }

  return (
    <MeetConference
      mintRequest={{ meetingId: meetingId ?? "" }}
      identity={meetingId ?? ""}
      shareLink={shareLink}
      renderOwnerActions={({ token }) => {
        // COSMETIC owner check only — gates whether the button renders. The real
        // owner gate is server-side (createMeetingGuestInvite returns a generic
        // 404 to non-owners). The flag is checked FIRST so `readModeratorClaim`
        // never runs when the feature is off (flag-off stays fully inert).
        if (!featureFlags.meetingGuestInvites || !readModeratorClaim(token)) {
          return null;
        }
        return (
          <button
            type="button"
            aria-label={t("standaloneMeet.copyGuestLink")}
            title={t("standaloneMeet.copyGuestLink")}
            onClick={handleCopyGuestLink}
            disabled={isCreatingGuestLink}
            className="p-1 text-white hover:bg-primary/50 disabled:opacity-50"
          >
            <UserPlus className="size-6" aria-hidden="true" />
          </button>
        );
      }}
    />
  );
}
